
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Provider, inject } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FeatureKeys, FeatureService } from "company/components/features/feature.service";
import moment from "moment";
import { CharDataElementComponent } from "rl-common/components/char-data/char-data-element.component";
import { CharDataElementUsage } from "rl-common/components/char-data/char-data.models";
import { CheckboxInputComponent } from "rl-common/components/checkbox-input/checkbox-input.component";
import { DateInputComponent } from "rl-common/components/date-input/date-input.component";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { ICharacteristicData } from "rl-common/models/i-characteristic-data";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { CharTypeId, SystemIndicators } from "rl-common/rl-common.consts";
import { AccountingProcessesService } from "rl-common/services/accounting-processes/accounting-processes.service";
import { ITaxManagementTaxRateResult } from "rl-common/services/accounting-processes/models/i-tax-management-tax-rate";
import { GrowlerService } from "rl-common/services/growler.service";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { Observable, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { ApTaxRateModalAdapter, IApTaxRateModalComponent } from "./ap-tax-rate-modal.adapter";

interface ITaxRateForm {
	taxRate: FormControl<number | null>;
	territoryXchrValId: FormControl<ICharacteristicData[] | null>;
	stateProvince: FormControl<string>;
	postalCode: FormControl<string>;
	effectiveDate: FormControl<string>;
	activeIndicator: FormControl<boolean>;
	taxCode: FormControl<string>;
}

@Component({
	selector: "rl-ap-tax-rate-modal",
	templateUrl: "./ap-tax-rate-modal.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, CharDataElementComponent, DateInputComponent, CheckboxInputComponent, TextInputComponent, FormTableRowComponent, FormTableControlDirective]
})
export class ApTaxRateModalComponent implements OnInit, OnDestroy, IApTaxRateModalComponent {
	private readonly _modal = inject(NgbActiveModal);
	private readonly _oneConfig = inject(OneConfigService);
	private readonly _accountingProcessesService = inject(AccountingProcessesService);
	private readonly _growlerService = inject(GrowlerService);
	private readonly _promptService = inject(PromptService);
	private readonly _featureService = inject(FeatureService);


	@Input()
	apTaxTypId: string;

	@Input()
	divId: number;

	@Input()
	existingRates: ITaxManagementTaxRateResult[] = [];

	@Input()
	taxRate: ITaxManagementTaxRateResult | null = null;

	@Output()
	onComplete = new EventEmitter<ITaxManagementTaxRateResult>();

	isSubmitting = false;
	territoryCmd: ICharacteristicMetaData | null = null;
	taxCodeLabel = "Tax Code";
	territoryLabel = "Territory";
	stateProvinceLabel = "State / Province";
	postalCodeLabel = "Zip / Postal Code";
	isAccProTaxManagementEnabled = false;
	CharDataElementUsage = CharDataElementUsage;

	get isEditing(): boolean {
		return !!this.taxRate?.apTaxRateId;
	}

	form = new FormGroup<ITaxRateForm>({
		taxRate: new FormControl<number | null>(null, [Validators.required]),
		taxCode: new FormControl<string>("", { nonNullable: true }),
		territoryXchrValId: new FormControl<ICharacteristicData[] | null>(null, [Validators.required]),
		stateProvince: new FormControl<string>("", { nonNullable: true }),
		postalCode: new FormControl<string>("", { nonNullable: true }),
		effectiveDate: new FormControl<string>("", { nonNullable: true, validators: [Validators.required] }),
		activeIndicator: new FormControl<boolean>(true, { nonNullable: true },
		)
	});

	private readonly _subs: Subscription[] = [];

	async ngOnInit(): Promise<void> {
		const featureSub = this._featureService.isEnabled$(FeatureKeys.AccProTaxMgmt).subscribe(res => {
			this.isAccProTaxManagementEnabled = res;
		});
		this._subs.push(featureSub);
		const countryChar = this._oneConfig.getCharTypeCmds(CharTypeId.Table)
			?.find(c => c.systemIndicator === SystemIndicators.AddressCountry) ?? null;
		if (countryChar) {
			this.territoryCmd = countryChar;
			this.territoryLabel = countryChar.label || "Territory";
		}

		const stateChar = this._oneConfig.getCharBySystemIndicator(CharTypeId.Table, SystemIndicators.AddressState);
		if (stateChar) {
			this.stateProvinceLabel = stateChar.label || "State / Province";
		}

		const postalChar = this._oneConfig.getCharBySystemIndicator(CharTypeId.Table, SystemIndicators.AddressPostalCode);
		if (postalChar) {
			this.postalCodeLabel = postalChar.label || "Zip / Postal Code";
		}

		if (this.taxRate) {
			const territoryCharData: ICharacteristicData[] = this.taxRate.territoryXchrValId ? [{
				charactersticID: this.territoryCmd?.characteristicID,
				recordCharacteristicID: 0,
				value: null,
				valueID: Number(this.taxRate.territoryXchrValId)
			}] : [];
			this.form.patchValue({
				taxRate: this.taxRate.taxRate,
				taxCode: this.taxRate.taxCode ?? "",
				territoryXchrValId: territoryCharData,
				stateProvince: this.taxRate.stateProvince ?? "",
				postalCode: this.taxRate.postalCode ?? "",
				effectiveDate: this.taxRate.effectiveDate,
				activeIndicator: this.taxRate.activeIndicator
			});
		}
	}

	get canSave(): boolean {
		return this.form.valid && !this.isSubmitting;
	}

	save(): void {
		if (!this.canSave) {
			return;
		}

		const fv = this.form.getRawValue();
		const normalizeStr = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
		const normalizeDate = (v: string | null | undefined) => {
			if (!v) {
 return ""; 
}
			const m = moment.utc(v, [moment.ISO_8601, "MM/DD/YYYY", "M/D/YYYY"], true);
			return m.isValid() ? m.format("YYYY-MM-DD") : v.split("T")[0];
		};

		const newTerritory = fv.territoryXchrValId?.[0]?.valueID?.toString() ?? "";
		const newTaxCode = fv.taxCode;
		const newState = normalizeStr(fv.stateProvince);
		const newZip = normalizeStr(fv.postalCode);
		const newDate = normalizeDate(fv.effectiveDate);

		const isDuplicate = this.existingRates.some(r => {
			if (this.isEditing && r.apTaxRateId === this.taxRate.apTaxRateId) {
				return false;
			}
			return r.territoryXchrValId === newTerritory
				&& r.taxCode === newTaxCode
				&& normalizeStr(r.stateProvince) === newState
				&& normalizeStr(r.postalCode) === newZip
				&& normalizeDate(r.effectiveDate) === newDate;
		});

		if (isDuplicate) {
			this._promptService.alert(
				this.isEditing ? "Update Failed" : "Create Failed",
				"Tax Type, Country, State, Zip and effective date already exists."
			);
			return;
		}

		const payload: ITaxManagementTaxRateResult = {
			apTaxRateId: this.taxRate?.apTaxRateId ?? null,
			divId: this.divId,
			apTaxTypId: this.apTaxTypId,
			taxRate: fv.taxRate,
			territoryXchrValId: newTerritory,
			taxCode: newTaxCode,
			stateProvince: fv.stateProvince.trim() || null,
			postalCode: fv.postalCode.trim() || null,
			effectiveDate: newDate,
			activeIndicator: fv.activeIndicator
		};

		this.isSubmitting = true;
		const op$: Observable<
			{ status: "success"; data: string | number | null } | { status: "error"; data: string }
		> = this.isEditing
			? this._accountingProcessesService.updateTaxRate(payload)
			: this._accountingProcessesService.createTaxRate(payload);

		const sub = op$.pipe(finalize(() => this.isSubmitting = false)).subscribe(response => {
			if (response.status === "success") {
				this._growlerService.success().growl(this.isEditing ? "Tax rate updated." : "Tax rate created.");
				this.onComplete.emit(payload);
			} else {
				this._promptService.alert(this.isEditing ? "Update Failed" : "Create Failed", response.data);
			}
		});
		this._subs.push(sub);
	}

	cancel(): void {
		this._modal.dismiss();
	}

	ngOnDestroy(): void {
		this._subs.forEach(s => s.unsubscribe());
	}
}

export const AP_TAXRATE_MODAL_PROVIDER: Provider = {
	provide: ApTaxRateModalAdapter,
	useFactory: () => new ApTaxRateModalAdapter(ApTaxRateModalComponent)
};
